import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ListingLink } from "./ListingLink";
import { resolveImg, track } from "@/lib/marketplaceTrack";

const INTERVAL = 6000;
const HOLD_AFTER_INTERACTION = 12000;
const POS = { center: "object-center", left: "object-left", right: "object-right", top: "object-top", bottom: "object-bottom" };

export function HeroCarousel({ slides }) {
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const [holdUntil, setHoldUntil] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set([0, 1]));
  const touch = useRef(null);
  const n = slides.length;
  const paused = hover || holdUntil > Date.now();

  const go = useCallback((i, manual = true) => {
    const next = (i + n) % n;
    setIdx(next);
    setLoaded((s) => new Set([...s, next, (next + 1) % n]));
    if (manual) setHoldUntil(Date.now() + HOLD_AFTER_INTERACTION);
  }, [n]);

  useEffect(() => {
    if (n < 2) return;
    const wait = paused ? Math.max(holdUntil - Date.now(), 0) + INTERVAL : INTERVAL;
    if (hover) return;
    const t = setTimeout(() => { if (!document.hidden) go(idx + 1, false); }, wait);
    return () => clearTimeout(t);
  }, [idx, hover, holdUntil, paused, n, go]);

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; setHoldUntil(Date.now() + HOLD_AFTER_INTERACTION); };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 45) go(idx + (dx < 0 ? 1 : -1));
    touch.current = null;
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") go(idx + 1);
    if (e.key === "ArrowLeft") go(idx - 1);
  };

  return (
    <section data-testid="mkt-carousel" aria-roledescription="carousel" aria-label="Featured HOH_Vibes products"
      tabIndex={0} onKeyDown={onKey}
      onPointerEnter={(e) => e.pointerType === "mouse" && setHover(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setHover(false)}
      onFocus={(e) => e.target.matches(":focus-visible") && setHover(true)} onBlur={() => setHover(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      className="relative grid overflow-hidden bg-[#0A0A0A] ambient-grain focus:outline-none select-none">
      {slides.map((s, i) => (
        <Slide key={s.id} slide={s} active={i === idx} load={loaded.has(i)} first={i === 0} label={`${i + 1} of ${n}`} />
      ))}
      {n > 1 && (
        <>
          <div className="absolute bottom-6 lg:bottom-10 left-0 right-0 z-20">
            <div className="max-w-[1360px] mx-auto px-6 lg:px-10 flex items-center gap-2.5" role="tablist" aria-label="Choose slide">
              {slides.map((s, i) => (
                <button key={s.id} type="button" role="tab" aria-selected={i === idx} aria-label={`Show ${s.title}`}
                  data-testid={`mkt-carousel-dot-${i}`} onClick={() => go(i)}
                  className={`relative h-1.5 rounded-full overflow-hidden transition-[width,background-color] duration-500 ${i === idx ? "w-12 bg-white/20" : "w-4 bg-white/30 hover:bg-white/60"}`}>
                  {i === idx && <span key={`${idx}-${paused}`} className={`absolute inset-y-0 left-0 bg-[#D4AF37] ${paused ? "w-full" : "mkt-progress"}`} style={{ animationDuration: `${INTERVAL}ms` }} />}
                </button>
              ))}
              <span className="ml-3 text-[10px] tracking-[0.25em] text-neutral-400 tabular-nums" data-testid="mkt-carousel-counter">
                {String(idx + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
              </span>
              <div className="ml-auto flex gap-2">
                <Arrow dir="prev" onClick={() => go(idx - 1)} />
                <Arrow dir="next" onClick={() => go(idx + 1)} />
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function Slide({ slide: s, active, load, first, label }) {
  const Heading = first ? "h1" : "h2";
  useEffect(() => { if (active) track("view", `slide-${s.id}`); }, [active, s.id]);
  const link = (url, k) => ({ id: `slide-${s.id}`, destination_url: url, k });
  return (
    <div role="group" aria-roledescription="slide" aria-label={label} aria-hidden={!active}
      data-testid={`mkt-slide-${s.id}`} data-active={active}
      className={`col-start-1 row-start-1 relative w-full min-h-[560px] h-[82svh] max-h-[720px] lg:max-h-none lg:h-[min(56.25vw,820px,88vh)] transition-opacity duration-1000 ease-out ${active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
      {load && (
        <img src={resolveImg(s.image_url)} alt={s.title} decoding="async" fetchpriority={first ? "high" : "low"}
          className={`absolute inset-0 w-full h-full object-cover ${POS[s.image_position] || POS.center} ${active ? "mkt-kenburns" : ""}`} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-black/20 lg:bg-gradient-to-r lg:from-[#0A0A0A]/95 lg:via-[#0A0A0A]/55 lg:to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      <div className="relative z-10 h-full max-w-[1360px] mx-auto px-6 lg:px-10 flex items-end lg:items-center pb-20 lg:pb-16 pt-24">
        <div key={active ? "on" : "off"} className={`max-w-2xl ${active ? "mkt-rise" : ""}`}>
          {(s.eyebrow || s.badge) && (
            <div className="flex flex-wrap items-center gap-3 mb-4 lg:mb-6">
              {s.eyebrow && <div className="label-eyebrow">{s.eyebrow}</div>}
              {s.badge && <span className="text-[10px] tracking-[0.25em] uppercase font-semibold px-2.5 py-1 rounded bg-amber-400 text-black" data-testid={`mkt-slide-badge-${s.id}`}>{s.badge}</span>}
            </div>
          )}
          <Heading className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.02] tracking-tight" data-testid={`mkt-slide-title-${s.id}`}>{s.title}</Heading>
          {s.description && <p className="mt-5 lg:mt-7 text-base lg:text-lg text-neutral-300 max-w-xl leading-relaxed">{s.description}</p>}
          <div className="mt-8 lg:mt-10 flex flex-wrap gap-3 lg:gap-4">
            {s.cta_label && s.cta_url && (
              <ListingLink listing={link(s.cta_url)} testid={`mkt-slide-cta-${s.id}`}
                className="btn-gold px-7 py-3.5 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold" tabIndex={active ? 0 : -1}>
                {s.cta_label} <ArrowRight size={15} />
              </ListingLink>
            )}
            {s.secondary_label && s.secondary_url && (
              <ListingLink listing={link(s.secondary_url)} testid={`mkt-slide-secondary-${s.id}`}
                className="btn-ghost-gold px-7 py-3.5 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold" tabIndex={active ? 0 : -1}>
                {s.secondary_label}
              </ListingLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Arrow({ dir, onClick }) {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={onClick} aria-label={dir === "prev" ? "Previous slide" : "Next slide"} data-testid={`mkt-carousel-${dir}`}
      className="hidden sm:flex w-12 h-12 items-center justify-center rounded-full border border-white/20 bg-black/40 backdrop-blur-md text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
      <Icon size={22} />
    </button>
  );
}
